<template>
  <div class="network-card-history-view" id="networkCardHistory">
    <!-- 数据展示区域 -->
    <div class="data-display-wrapper">
      <!-- 加载状态 -->
      <Spin v-if="loading" fix>
        <div class="loading-text">
          <Icon type="ios-loading" size="30" class="spin-icon-load"></Icon>
          <div>{{ langMap.dashboard_loading[lang] }}</div>
        </div>
      </Spin>
      
      <!-- 空状态 -->
      <div v-if="!loading && !hasData" class="empty-state">
        <div class="empty-icon-wrapper">
          <div class="hologram-ring"></div>
          <div class="hologram-ring delay-1"></div>
          <div class="hologram-ring delay-2"></div>
          <div class="empty-icon">🌐</div>
        </div>
        <div class="empty-text">{{ langMap.empty[lang] }}</div>
        <div class="empty-subtext">{{ emptyMessage }}</div>
      </div>
      
      <!-- 数据表格 -->
      <div v-if="!loading && hasData" class="data-content">
        <div class="table-wrapper">
          <Table
            :columns="columns"
            :data="tableData"
            stripe
            border
            size="small"
            :height="tableHeight"
          >
            <template slot-scope="{ row }" slot="dataTime">
              {{ formatTime(row.dataTime) }}
            </template>
            <template slot-scope="{ row }" slot="statusData">
              <div v-if="row.nicList && row.nicList.length" class="status-data-cell">
                <div
                  v-for="(nic, index) in row.nicList"
                  :key="index"
                  class="status-item"
                  :class="{
                    'status-enabled': nic.status === 'Up',
                    'status-disabled': nic.status === 'Down'
                  }"
                >
                  <span class="status-badge">{{ index + 1 }}</span>
                  <span class="status-text">{{ nic.status === 'Up' ? langMap.dashboard_enabled[lang] : langMap.dashboard_disabled[lang] }}</span>
                </div>
              </div>
              <span v-else>-</span>
            </template>
            <template slot-scope="{ row }" slot="nicData">
              <div v-if="row.nicList && row.nicList.length" class="nic-data-cell">
                <div
                  v-for="(nic, index) in row.nicList"
                  :key="index"
                  class="nic-item-inline"
                >
                  <span class="nic-badge">{{ index + 1 }}</span>
                  <span class="nic-name">{{ nic.deviceName }}</span>
                  <span class="nic-divider">|</span>
                  <span class="metric-item bandwidth">
                    <i class="metric-icon">⚡</i>
                    <span class="metric-label">{{ langMap.dashboard_bandwidth[lang] }}:</span>
                    <span class="metric-value">{{ formatValue(nic.bandWidth) }}</span>
                    <em>Mbps</em>
                  </span>
                  <span class="nic-divider">|</span>
                  <span class="metric-item upload">
                    <i class="metric-icon">↑</i>
                    <span class="metric-label">{{ langMap.dashboard_upload[lang] }}:</span>
                    <span class="metric-value">{{ formatValue(nic.throughputUp) }}</span>
                    <em>Mbps</em>/
                    <span class="metric-value">{{ formatValue(nic.usageUp) }}</span>
                    <em>%</em>
                  </span>
                  <span class="nic-divider">|</span>
                  <span class="metric-item download">
                    <i class="metric-icon">↓</i>
                    <span class="metric-label">{{ langMap.dashboard_download[lang] }}:</span>
                    <span class="metric-value">{{ formatValue(nic.throughputDown) }}</span>
                    <em>Mbps</em>/
                    <span class="metric-value">{{ formatValue(nic.usageDown) }}</span>
                    <em>%</em>
                  </span>
                  <span class="nic-divider">|</span>
                  <span class="metric-item latency">
                    <i class="metric-icon">⏱</i>
                    <span class="metric-label">{{ langMap.dashboard_latency[lang] }}:</span>
                    <span class="metric-value">{{ formatValue(nic.latency) }}</span>
                    <em>ms</em>
                  </span>
                  <span class="nic-divider">|</span>
                  <span class="metric-item jitter">
                    <i class="metric-icon">〰</i>
                    <span class="metric-label">{{ langMap.dashboard_jitter[lang] }}:</span>
                    <span class="metric-value">{{ formatValue(nic.jitter) }}</span>
                    <em>ms</em>
                  </span>
                  <span class="nic-divider">|</span>
                  <span class="metric-item loss">
                    <i class="metric-icon">✕</i>
                    <span class="metric-label">{{ langMap.dashboard_packetLoss[lang] }}:</span>
                    <span class="metric-value">{{ formatValue(nic.packetLossRate) }}</span>
                    <em>%</em>
                  </span>
                </div>
              </div>
              <span v-else>-</span>
            </template>
          </Table>
        </div>
        
        <!-- 自定义分页器 -->
        <div class="pagination-wrapper">
          <div class="cyber-pagination">
            <!-- 总数信息 -->
            <div class="pagination-info">
              <span class="info-label">{{ langMap.dashboard_totalRecords[lang] }}</span>
              <span class="info-value">{{ pagination.total }}</span>
              <span class="info-divider">|</span>
              <span class="info-text">{{ pageRangeText }}</span>
            </div>
            
            <!-- 页码按钮组 -->
            <div class="pagination-controls">
              <!-- 首页 -->
              <button 
                class="page-btn page-first"
                :disabled="pagination.current === 1"
                @click="handlePageChange(1)"
              >
                <span class="btn-icon">《《</span>
              </button>
              
              <!-- 上一页 -->
              <button 
                class="page-btn page-prev"
                :disabled="pagination.current === 1"
                @click="handlePageChange(pagination.current - 1)"
              >
                <span class="btn-icon">‹</span>
              </button>
              
              <!-- 页码按钮 -->
              <div class="page-numbers">
                <button
                  v-for="page in visiblePages"
                  :key="page"
                  :class="['page-number', { active: page === pagination.current, ellipsis: page === '...' }]"
                  :disabled="page === '...'"
                  @click="page !== '...' && handlePageChange(page)"
                >
                  <span class="number-glow" v-if="page === pagination.current"></span>
                  {{ page }}
                </button>
              </div>
              
              <!-- 下一页 -->
              <button 
                class="page-btn page-next"
                :disabled="pagination.current === totalPages"
                @click="handlePageChange(pagination.current + 1)"
              >
                <span class="btn-icon">›</span>
              </button>
              
              <!-- 尾页 -->
              <button 
                class="page-btn page-last"
                :disabled="pagination.current === totalPages"
                @click="handlePageChange(totalPages)"
              >
                <span class="btn-icon">》》</span>
              </button>
            </div>
            
            <!-- 每页条数选择 -->
            <div class="page-size-selector">
              <span class="selector-label">{{ langMap.dashboard_perPage[lang] }}</span>
              <div class="size-options">
                <button
                  v-for="size in pageSizeOptions"
                  :key="size"
                  :class="['size-option', { active: pagination.size === size }]"
                  @click="handlePageSizeChange(size)"
                >
                  {{ size }}
                </button>
              </div>
            </div>
            
            <!-- 快速跳转 -->
            <div class="page-jumper">
              <span class="jumper-label">{{ langMap.dashboard_jumpTo[lang] }}</span>
              <input 
                type="number" 
                class="jumper-input"
                v-model.number="jumpPage"
                @keyup.enter="handleJumpPage"
                :placeholder="String(pagination.current)"
                min="1"
                :max="totalPages"
              />
              <button class="jumper-btn" @click="handleJumpPage">
                <span class="btn-arrow">→</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import langMap from '@/utils/language'
import { mapState } from 'vuex';

export default {
  name: 'NetworkCardHistoryView',
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    hasData: {
      type: Boolean,
      default: false
    },
    emptyMessage: {
      type: String,
      default: ''
    },
    tableData: {
      type: Array,
      default: () => []
    },
    pagination: {
      type: Object,
      default: () => ({
        total: 0,
        current: 1,
        size: 20,
        pages: 0
      })
    }
  },
  data() {
    return {
      langMap,
      tableHeight: 500,
      pageSizeOptions: [10, 20, 30, 50],
      jumpPage: null,
      windowHeight: window.innerHeight,
      columns: [
        {
          title: langMap.dashboard_dataTime[this.$store.state.app.language],
          slot: 'dataTime',
          width: 180,
          align: 'center'
        },
        {
          title: langMap.dashboard_enableStatus[this.$store.state.app.language],
          slot: 'statusData',
          width: 120,
          align: 'center'
        },
        {
          title: langMap.dashboard_networkInfoColumn[this.$store.state.app.language],
          slot: 'nicData',
          minWidth: 600,
          align: 'left'
        },
        {
          title: langMap.dashboard_robotCode[this.$store.state.app.language],
          key: 'robotCode',
          width: 150,
          align: 'center'
        }
      ]
    }
  },
  computed: {
    ...mapState({
      lang: state => state.app.language,
    }),
    tableHeight() {
      return Math.max(400, this.windowHeight - 502);
    },
    totalPages() {
      return Math.ceil(this.pagination.total / this.pagination.size) || 1;
    },
    pageRangeText() {
      const start = (this.pagination.current - 1) * this.pagination.size + 1;
      const end = Math.min(this.pagination.current * this.pagination.size, this.pagination.total);
      return `${start}-${end}`;
    },
    visiblePages() {
      const current = this.pagination.current;
      const total = this.totalPages;
      const pages = [];
      
      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        if (current <= 4) {
          for (let i = 1; i <= 5; i++) {
            pages.push(i);
          }
          pages.push('...');
          pages.push(total);
        } else if (current >= total - 3) {
          pages.push(1);
          pages.push('...');
          for (let i = total - 4; i <= total; i++) {
            pages.push(i);
          }
        } else {
          pages.push(1);
          pages.push('...');
          for (let i = current - 1; i <= current + 1; i++) {
            pages.push(i);
          }
          pages.push('...');
          pages.push(total);
        }
      }
      
      return pages;
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    this.updateColumnsTitle();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  watch: {
    lang() {
      this.updateColumnsTitle();
    }
  },
  methods: {
    updateColumnsTitle() {
      this.columns[0].title = this.langMap.dashboard_dataTime[this.lang];
      this.columns[1].title = this.langMap.dashboard_enableStatus[this.lang];
      this.columns[2].title = this.langMap.dashboard_networkInfoColumn[this.lang];
      this.columns[3].title = this.langMap.dashboard_robotCode[this.lang];
    },
    formatTime(timestamp) {
      if (!timestamp) return '-';
      const date = new Date(timestamp);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },
    formatValue(value) {
      if (value === null || value === undefined || value === '') {
        return '-';
      }
      const num = parseFloat(value);
      if (isNaN(num)) {
        return value;
      }
      return parseFloat(num.toFixed(2));
    },
    handlePageChange(page) {
      this.$emit('page-change', page);
    },
    handlePageSizeChange(size) {
      this.$emit('size-change', size);
    },
    handleJumpPage() {
      if (!this.jumpPage || this.jumpPage < 1 || this.jumpPage > this.totalPages) {
        this.$Message.warning(this.langMap.dashboard_inputValidPageRange[this.lang].replace('{max}', this.totalPages));
        return;
      }
      this.handlePageChange(this.jumpPage);
      this.jumpPage = null;
    },
    handleResize() {
      this.windowHeight = window.innerHeight;
    }
  }
}
</script>

<style lang="less" scoped>
#networkCardHistory {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  /deep/.ivu-table-cell{
    padding-left:5px
  }
  .data-display-wrapper {
    .table-wrapper {
        .nic-data-cell {
          display: flex;
          flex-direction: column;
          gap: 6px;
          padding: 2px 0;
        }
        
        .status-data-cell {
          display: flex;
          flex-direction: column;
          gap: 6px;
          padding: 4px 0;
          
          .status-item {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            padding: 4px 0;
            
            .status-badge {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              width: 18px;
              height: 18px;
              border-radius: 50%;
              font-size: 10px;
              font-weight: 700;
              background: rgba(0, 240, 255, 0.15);
              border: 1px solid rgba(0, 240, 255, 0.4);
              color: #00f0ff;
              text-shadow: 0 0 3px rgba(0, 240, 255, 0.6);
            }
            
            .status-text {
              display: inline-flex;
              align-items: center;
              gap: 4px;
              padding: 2px 10px;
              border-radius: 12px;
              font-size: 12px;
              font-weight: 600;
              white-space: nowrap;
              transition: all 0.2s ease;
              
              &::before {
                content: '';
                display: inline-block;
                width: 6px;
                height: 6px;
                border-radius: 50%;
                transition: all 0.2s ease;
              }
            }
            
            &.status-enabled {
              .status-text {
                background: linear-gradient(135deg, rgba(82, 196, 26, 0.15), rgba(82, 196, 26, 0.08));
                border: 1px solid rgba(82, 196, 26, 0.4);
                color: #52c41a;
                box-shadow: 0 2px 4px rgba(82, 196, 26, 0.1);
                
                &::before {
                  background: #52c41a;
                  box-shadow: 0 0 6px rgba(82, 196, 26, 0.8);
                  animation: pulse-dot 2s ease-in-out infinite;
                }
              }
            }
            
            &.status-disabled {
              .status-text {
                background: linear-gradient(135deg, rgba(128, 128, 128, 0.12), rgba(128, 128, 128, 0.06));
                border: 1px solid rgba(128, 128, 128, 0.3);
                color: rgba(180, 180, 180, 0.85);
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                
                &::before {
                  background: rgba(128, 128, 128, 0.6);
                }
              }
            }
          }
          
          @keyframes pulse-dot {
            0%, 100% {
              opacity: 0.6;
              transform: scale(1);
            }
            50% {
              opacity: 1;
              transform: scale(1.2);
            }
          }
        }
        
        .nic-item-inline {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 12px;
          background: linear-gradient(90deg, rgba(0, 240, 255, 0.03), rgba(0, 120, 255, 0.05));
          border-left: 3px solid rgba(0, 240, 255, 0.5);
          border-radius: 4px;
          font-family: 'Courier New', monospace;
          transition: all 0.2s ease;
          
          &:hover {
            background: linear-gradient(90deg, rgba(0, 240, 255, 0.08), rgba(0, 120, 255, 0.1));
            border-left-color: #00f0ff;
            box-shadow: 0 2px 8px rgba(0, 240, 255, 0.2);
          }
          
          .nic-badge {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-width: 24px;
            height: 24px;
            padding: 0 6px;
            background: rgba(0, 240, 255, 0.15);
            border: 1px solid rgba(0, 240, 255, 0.4);
            border-radius: 12px;
            color: #00f0ff;
            font-size: 12px;
            font-weight: 700;
            text-shadow: 0 0 5px rgba(0, 240, 255, 0.8);
          }
          
          .nic-name {
            font-size: 13px;
            font-weight: 600;
            color: #e6f7ff;
            white-space: nowrap;
          }
          
          .nic-divider {
            color: rgba(0, 240, 255, 0.3);
            font-weight: 300;
            font-size: 13px;
          }
          
          .metric-item {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            white-space: nowrap;
            
            .metric-icon {
              font-style: normal;
              font-size: 16px;
              margin-right: 3px;
              display: inline-block;
            }
            
            .metric-label {
              font-size: 12px;
              color: rgba(255, 255, 255, 0.7);
              font-weight: 500;
            }
            
            .metric-value {
              font-size: 13px;
              color: rgba(255, 255, 255, 0.95);
              font-weight: 600;
            }
            
            em {
              font-style: normal;
              font-size: 11px;
              color: rgba(255, 255, 255, 0.6);
              margin-left: 1px;
            }
            
            &.bandwidth .metric-icon {
              color: #00f0ff;
              filter: drop-shadow(0 0 3px rgba(0, 240, 255, 0.6));
              animation: pulse 2s ease-in-out infinite;
            }
            
            &.upload .metric-icon {
              color: #52c41a;
              filter: drop-shadow(0 0 3px rgba(82, 196, 26, 0.6));
              animation: bounceUp 1.5s ease-in-out infinite;
            }
            
            &.download .metric-icon {
              color: #1890ff;
              filter: drop-shadow(0 0 3px rgba(24, 144, 255, 0.6));
              animation: bounceDown 1.5s ease-in-out infinite;
            }
            
            &.latency .metric-icon {
              color: #faad14;
              filter: drop-shadow(0 0 3px rgba(250, 173, 20, 0.6));
              animation: spin 3s linear infinite;
            }
            
            &.jitter .metric-icon {
              color: #fa541c;
              filter: drop-shadow(0 0 3px rgba(250, 84, 28, 0.6));
              animation: shake 2s ease-in-out infinite;
            }
            
            &.loss .metric-icon {
              color: #f5222d;
              filter: drop-shadow(0 0 3px rgba(245, 34, 45, 0.6));
              animation: blink 1.5s ease-in-out infinite;
            }
            
            &.enable-status {
              &.status-up {
                .metric-icon {
                  color: #52c41a;
                  filter: drop-shadow(0 0 3px rgba(82, 196, 26, 0.6));
                }
                .metric-value {
                  color: #52c41a;
                  font-weight: 700;
                  text-shadow: 0 0 5px rgba(82, 196, 26, 0.5);
                }
              }
              
              &.status-down {
                .metric-icon {
                  color: #ff4d4f;
                  filter: drop-shadow(0 0 3px rgba(255, 77, 79, 0.6));
                }
                .metric-value {
                  color: #ff4d4f;
                  font-weight: 700;
                  text-shadow: 0 0 5px rgba(255, 77, 79, 0.5);
                }
              }
            }
          }
        }
        
        // 动画定义
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            filter: drop-shadow(0 0 3px rgba(0, 240, 255, 0.6));
          }
          50% {
            transform: scale(1.1);
            filter: drop-shadow(0 0 6px rgba(0, 240, 255, 0.9));
          }
        }
        
        @keyframes bounceUp {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-3px);
          }
        }
        
        @keyframes bounceDown {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(3px);
          }
        }
        
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        @keyframes shake {
          0%, 100% {
            transform: translateX(0);
          }
          25% {
            transform: translateX(-1px);
          }
          75% {
            transform: translateX(1px);
          }
        }
        
        @keyframes blink {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.4;
          }
        }
      
      .pagination-wrapper {
        padding: 0;
        margin-top: 10px;
      }
      
      .cyber-pagination {
        display: flex;
        align-items: center;
        gap: 20px;
        padding: 5px 25px;
        background: linear-gradient(135deg, rgba(0, 21, 41, 0.9), rgba(0, 40, 80, 0.8));
        border: 2px solid rgba(0, 240, 255, 0.3);
        border-radius: 12px;
        backdrop-filter: blur(10px);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(0, 240, 255, 0.2);
        position: relative;
        overflow: hidden;
        
        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 2px;
          background: linear-gradient(90deg, transparent, #00f0ff, transparent);
          animation: scanLine 3s linear infinite;
        }
        
        .pagination-info {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 16px;
          background: rgba(0, 240, 255, 0.05);
          border: 1px solid rgba(0, 240, 255, 0.3);
          border-radius: 8px;
          font-family: 'Courier New', monospace;
          
          .info-label {
            font-size: 11px;
            color: rgba(0, 240, 255, 0.6);
            font-weight: 700;
            letter-spacing: 1px;
          }
          
          .info-value {
            font-size: 18px;
            color: #00f0ff;
            font-weight: 700;
            text-shadow: 0 0 10px rgba(0, 240, 255, 0.8);
          }
          
          .info-divider {
            color: rgba(0, 240, 255, 0.4);
            margin: 0 5px;
          }
          
          .info-text {
            font-size: 13px;
            color: rgba(255, 255, 255, 0.7);
          }
        }
        
        .pagination-controls {
          display: flex;
          align-items: center;
          gap: 8px;
          flex: 1;
          justify-content: center;
          
          .page-btn {
            width: 36px;
            height: 36px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(0, 40, 80, 0.6);
            border: 1px solid rgba(0, 240, 255, 0.3);
            border-radius: 6px;
            color: rgba(0, 240, 255, 0.8);
            font-size: 16px;
            cursor: pointer;
            transition: all 0.3s ease;
            position: relative;
            overflow: hidden;
            
            &::before {
              content: '';
              position: absolute;
              top: 50%;
              left: 50%;
              width: 0;
              height: 0;
              border-radius: 50%;
              background: rgba(0, 240, 255, 0.3);
              transform: translate(-50%, -50%);
              transition: width 0.4s ease, height 0.4s ease;
            }
            
            &:hover:not(:disabled) {
              border-color: #00f0ff;
              color: #00f0ff;
              box-shadow: 0 0 15px rgba(0, 240, 255, 0.4);
              transform: translateY(-2px);
              
              &::before {
                width: 100%;
                height: 100%;
              }
            }
            
            &:disabled {
              opacity: 0.3;
              cursor: not-allowed;
              border-color: rgba(0, 240, 255, 0.1);
              color: rgba(0, 240, 255, 0.3);
            }
            
            .btn-icon {
              position: relative;
              z-index: 1;
            }
          }
          
          .page-numbers {
            display: flex;
            align-items: center;
            gap: 6px;
            
            .page-number {
              min-width: 40px;
              height: 40px;
              display: flex;
              align-items: center;
              justify-content: center;
              background: rgba(0, 30, 60, 0.7);
              border: 1px solid rgba(0, 240, 255, 0.2);
              border-radius: 8px;
              color: rgba(255, 255, 255, 0.7);
              font-size: 14px;
              font-weight: 600;
              font-family: 'Courier New', monospace;
              cursor: pointer;
              transition: all 0.3s ease;
              position: relative;
              overflow: hidden;
              
              &:hover:not(.active):not(.ellipsis) {
                border-color: rgba(0, 240, 255, 0.6);
                color: #00f0ff;
                background: rgba(0, 50, 80, 0.8);
                transform: scale(1.1);
                box-shadow: 0 0 12px rgba(0, 240, 255, 0.3);
              }
              
              &.active {
                background: linear-gradient(135deg, rgba(0, 240, 255, 0.3), rgba(0, 180, 255, 0.4));
                border-color: #00f0ff;
                color: #fff;
                box-shadow: 0 0 20px rgba(0, 240, 255, 0.6), inset 0 0 15px rgba(0, 240, 255, 0.2);
                transform: scale(1.15);
                
                .number-glow {
                  position: absolute;
                  top: 0;
                  left: 0;
                  right: 0;
                  bottom: 0;
                  background: radial-gradient(circle, rgba(0, 240, 255, 0.4), transparent);
                  animation: numberGlow 2s ease-in-out infinite;
                }
              }
              
              &.ellipsis {
                cursor: default;
                border-color: transparent;
                background: transparent;
                color: rgba(0, 240, 255, 0.5);
                
                &:hover {
                  transform: none;
                  box-shadow: none;
                }
              }
            }
          }
        }
        
        .page-size-selector {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 6px 12px;
          background: rgba(0, 240, 255, 0.05);
          border: 1px solid rgba(0, 240, 255, 0.2);
          border-radius: 8px;
          
          .selector-label {
            font-size: 11px;
            color: rgba(0, 240, 255, 0.6);
            font-weight: 700;
            letter-spacing: 1px;
            font-family: 'Courier New', monospace;
          }
          
          .size-options {
            display: flex;
            gap: 4px;
            
            .size-option {
              min-width: 32px;
              height: 28px;
              padding: 0 10px;
              display: flex;
              align-items: center;
              justify-content: center;
              background: rgba(0, 30, 60, 0.6);
              border: 1px solid rgba(0, 240, 255, 0.2);
              border-radius: 6px;
              color: rgba(255, 255, 255, 0.6);
              font-size: 12px;
              font-weight: 600;
              font-family: 'Courier New', monospace;
              cursor: pointer;
              transition: all 0.2s ease;
              
              &:hover:not(.active) {
                border-color: rgba(0, 240, 255, 0.5);
                color: #00f0ff;
                background: rgba(0, 50, 80, 0.7);
              }
              
              &.active {
                background: rgba(0, 240, 255, 0.2);
                border-color: #00f0ff;
                color: #00f0ff;
                box-shadow: 0 0 10px rgba(0, 240, 255, 0.3);
              }
            }
          }
        }
        
        .page-jumper {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 6px 12px;
          background: rgba(0, 240, 255, 0.05);
          border: 1px solid rgba(0, 240, 255, 0.2);
          border-radius: 8px;
          
          .jumper-label {
            font-size: 11px;
            color: rgba(0, 240, 255, 0.6);
            font-weight: 700;
            letter-spacing: 1px;
            font-family: 'Courier New', monospace;
          }
          
          .jumper-input {
            width: 50px;
            height: 32px;
            padding: 0 8px;
            background: rgba(0, 30, 60, 0.7);
            border: 1px solid rgba(0, 240, 255, 0.3);
            border-radius: 6px;
            color: #fff;
            font-size: 13px;
            font-weight: 600;
            font-family: 'Courier New', monospace;
            text-align: center;
            outline: none;
            transition: all 0.3s ease;
            
            &:focus {
              border-color: #00f0ff;
              box-shadow: 0 0 10px rgba(0, 240, 255, 0.4);
            }
            
            &::placeholder {
              color: rgba(255, 255, 255, 0.3);
            }
            
            &::-webkit-inner-spin-button,
            &::-webkit-outer-spin-button {
              -webkit-appearance: none;
              margin: 0;
            }
          }
          
          .jumper-btn {
            width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(0, 240, 255, 0.1);
            border: 1px solid rgba(0, 240, 255, 0.4);
            border-radius: 6px;
            color: #00f0ff;
            font-size: 16px;
            cursor: pointer;
            transition: all 0.3s ease;
            
            &:hover {
              background: rgba(0, 240, 255, 0.2);
              border-color: #00f0ff;
              box-shadow: 0 0 15px rgba(0, 240, 255, 0.4);
              transform: translateX(3px);
            }
            
            .btn-arrow {
              font-weight: 700;
            }
          }
        }
      }
      
      @keyframes scanLine {
        0% {
          left: -100%;
        }
        100% {
          left: 100%;
        }
      }
      
      @keyframes numberGlow {
        0%, 100% {
          opacity: 0.3;
        }
        50% {
          opacity: 0.8;
        }
      }
    }
  }
}
</style>
